import React, { useEffect, useState, useCallback } from 'react'
import { AdminLayout } from '../components/AdminLayout'
import { adminApi } from '../services/adminApi'
import {
  AlertCircle,
  CheckCircle,
  Clock,
  FileText,
  RefreshCw,
  TrendingDown,
  TrendingUp,
  XCircle
} from 'lucide-react'

export const SystemPage = () => {
  const [health, setHealth] = useState(null)
  const [logs, setLogs] = useState([])
  const [performanceStats, setPerformanceStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [logFilters, setLogFilters] = useState({
    level: '',
    hours: 24,
    limit: 100
  })

  const fetchSystemData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const [healthData, logsData, perfData] = await Promise.all([
        adminApi.getSystemHealth(),
        adminApi.getLogs(logFilters),
        adminApi.getPerformanceStats(logFilters.hours)
      ])
      setHealth(healthData)
      setLogs(logsData.logs || [])
      setPerformanceStats(perfData.performance_stats || {})
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [logFilters])

  useEffect(() => {
    fetchSystemData()
  }, [fetchSystemData])

  const getHealthColor = (status) => {
    const colors = {
      healthy: 'bg-green-100 text-green-800',
      degraded: 'bg-yellow-100 text-yellow-800',
      unhealthy: 'bg-red-100 text-red-800'
    }
    return colors[status] || 'bg-gray-100 text-gray-800'
  }

  const getLogLevelColor = (level) => {
    const colors = {
      ERROR: 'bg-red-100 text-red-800',
      WARN: 'bg-yellow-100 text-yellow-800',
      INFO: 'bg-blue-100 text-blue-800',
      DEBUG: 'bg-gray-100 text-gray-800'
    }
    return colors[level] || 'bg-gray-100 text-gray-800'
  }

  return (
    <AdminLayout>
      <div className="p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">System</h1>
            <p className="text-gray-500 mt-1">Monitor system health and logs</p>
          </div>
          <button
            onClick={fetchSystemData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-800">Error loading system data</p>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        )}

        {/* System Health */}
        {health && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className={`h-12 w-12 rounded-lg bg-${health.health_status === 'healthy' ? 'green' : health.health_status === 'degraded' ? 'yellow' : 'red'}-50 flex items-center justify-center`}>
                  {health.health_status === 'healthy' ? (
                    <CheckCircle className="h-6 w-6 text-green-600" />
                  ) : health.health_status === 'degraded' ? (
                    <AlertCircle className="h-6 w-6 text-yellow-600" />
                  ) : (
                    <XCircle className="h-6 w-6 text-red-600" />
                  )}
                </div>
              </div>
              <h3 className="text-sm font-medium text-gray-600">System Health</h3>
              <p className="text-2xl font-bold text-gray-900 mt-1">{health.health_score}%</p>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-2 ${getHealthColor(health.health_status)}`}>
                {health.health_status}
              </span>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="h-12 w-12 rounded-lg bg-red-50 flex items-center justify-center">
                  <AlertCircle className="h-6 w-6 text-red-600" />
                </div>
              </div>
              <h3 className="text-sm font-medium text-gray-600">Recent Errors</h3>
              <p className="text-2xl font-bold text-gray-900 mt-1">{health.recent_errors || 0}</p>
              <p className="text-sm text-gray-500 mt-1">Last hour</p>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="h-12 w-12 rounded-lg bg-orange-50 flex items-center justify-center">
                  <Clock className="h-6 w-6 text-orange-600" />
                </div>
              </div>
              <h3 className="text-sm font-medium text-gray-600">Slow Operations</h3>
              <p className="text-2xl font-bold text-gray-900 mt-1">{health.slow_operations || 0}</p>
              <p className="text-sm text-gray-500 mt-1">{'>'} 5s response time</p>
            </div>
          </div>
        )}

        {/* Performance Stats */}
        {performanceStats && Object.keys(performanceStats).length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-8">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Performance Statistics</h2>
              <p className="text-sm text-gray-500 mt-1">Last {logFilters.hours} hours</p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Count
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Avg Time (ms)
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Min / Max (ms)
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {Object.entries(performanceStats).map(([category, stats]) => (
                    <tr key={category} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{category}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{stats.count || 0}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-900">
                          {stats.avg_ms >= 1000 ? (
                            <TrendingUp className="h-4 w-4 mr-1 text-red-500" />
                          ) : (
                            <TrendingDown className="h-4 w-4 mr-1 text-green-500" />
                          )}
                          {Math.round(stats.avg_ms || 0)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">
                          {Math.round(stats.min_ms || 0)} / {Math.round(stats.max_ms || 0)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Logs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">System Logs</h2>
              <div className="flex gap-3">
                <select
                  value={logFilters.level}
                  onChange={(e) => setLogFilters({ ...logFilters, level: e.target.value })}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All Levels</option>
                  <option value="ERROR">Error</option>
                  <option value="WARN">Warning</option>
                  <option value="INFO">Info</option>
                  <option value="DEBUG">Debug</option>
                </select>
                <select
                  value={logFilters.hours}
                  onChange={(e) => setLogFilters({ ...logFilters, hours: parseInt(e.target.value) })}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value={1}>Last Hour</option>
                  <option value={6}>Last 6 Hours</option>
                  <option value={24}>Last 24 Hours</option>
                  <option value={168}>Last Week</option>
                </select>
              </div>
            </div>
          </div>
          <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
            {loading ? (
              <div className="p-12 text-center text-gray-500">
                <div className="flex items-center justify-center">
                  <RefreshCw className="h-6 w-6 animate-spin text-blue-600 mr-2" />
                  Loading logs...
                </div>
              </div>
            ) : logs.length === 0 ? (
              <div className="p-12 text-center text-gray-500">No logs found</div>
            ) : (
              logs.map((log) => (
                <div key={log.id} className="p-4 hover:bg-gray-50">
                  <div className="flex items-start gap-3">
                    <FileText className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getLogLevelColor(log.level)}`}>
                          {log.level}
                        </span>
                        {log.category && (
                          <span className="text-xs text-gray-500">{log.category}</span>
                        )}
                        {log.operation && (
                          <span className="text-xs text-gray-500">• {log.operation}</span>
                        )}
                        <span className="text-xs text-gray-400">
                          {new Date(log.timestamp).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-sm text-gray-900">{log.message}</p>
                      {log.details && Object.keys(log.details).length > 0 && (
                        <div className="mt-2 text-xs text-gray-600 bg-gray-50 p-2 rounded font-mono">
                          {JSON.stringify(log.details, null, 2)}
                        </div>
                      )}
                      {log.duration_ms && (
                        <div className="mt-1 text-xs text-gray-500">
                          Duration: {log.duration_ms}ms
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  )
}
